import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Settings,
  Sliders,
  Brain,
  Building2,
  Save,
  RefreshCw,
  Target,
  Award,
  MapPin,
  DollarSign,
  Users,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Info,
  Percent,
  Plus,
  X,
} from 'lucide-react';
import { v2 } from '@/lib/v2Api';
import { toast } from 'sonner';
import { v2Api } from '@/lib/v2Api';
import { confirm } from '@/lib/confirm';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ExampleLibrary — admin-curated {sentence, ideal_summary} pairs per
 * subsection. Up to 2 of these are injected into the Gemini summary
 * prompt as few-shot examples (rfp_pipeline.summarize_section). The
 * model gets a clear style + level-of-detail reference, lifting both
 * accuracy and consistency on engineering-tender boilerplate
 * (eligibility tables, PBG clauses, BoQ instructions etc).
 */
function ExampleLibrary({ examples, onChange }) {
  const [draftSentence, setDraftSentence] = useState("");
  const [draftIdeal, setDraftIdeal] = useState("");

  const addExample = () => {
    const s = draftSentence.trim();
    const i = draftIdeal.trim();
    if (!s || !i) return;
    onChange([...examples, { sentence: s, ideal_summary: i }]);
    setDraftSentence(""); setDraftIdeal("");
  };
  const removeExample = (idx) => {
    onChange(examples.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-wide text-slate-500 block">
        Few-shot examples for the AI summary
        <span className="ml-1 text-slate-400 normal-case">
          (up to 5 stored; the 2 most-relevant get sent to Gemini per call)
        </span>
      </label>
      {examples.length > 0 && (
        <div className="space-y-1.5">
          {examples.map((ex, i) => (
            <div key={i} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded p-2">
              <div className="flex-1 min-w-0 space-y-1">
                <div className="text-[10px] text-slate-500">
                  Example clause:
                </div>
                <div className="text-xs text-slate-800 italic">"{ex.sentence}"</div>
                <div className="text-[10px] text-slate-500 mt-1">
                  Ideal summary:
                </div>
                <div className="text-xs text-emerald-800">{ex.ideal_summary}</div>
              </div>
              <button
                type="button"
                onClick={() => removeExample(i)}
                className="text-slate-400 hover:text-rose-600 shrink-0"
                title="Remove this example"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {examples.length < 5 && (
        <div className="border border-dashed border-slate-300 rounded p-2 space-y-1.5">
          <textarea
            value={draftSentence}
            onChange={(e) => setDraftSentence(e.target.value)}
            placeholder="Paste a representative clause from past RFPs (e.g. 'The successful bidder shall furnish a Performance Security Deposit @ 3% of Annualised Contract Value...')"
            rows={2}
            className="w-full text-xs px-2 py-1 border border-slate-200 rounded outline-none focus:border-blue-400 resize-y"
          />
          <textarea
            value={draftIdeal}
            onChange={(e) => setDraftIdeal(e.target.value)}
            placeholder="Write the bid-manager-grade summary you'd want for that clause (e.g. 'PBG: 3% of Annualised Contract Value · BG/DD/BC form · valid 3 months beyond contract')"
            rows={2}
            className="w-full text-xs px-2 py-1 border border-slate-200 rounded outline-none focus:border-blue-400 resize-y"
          />
          <div className="flex justify-end">
            <button
              type="button"
              onClick={addExample}
              disabled={!draftSentence.trim() || !draftIdeal.trim()}
              className="text-[10px] px-2 py-0.5 rounded bg-blue-600 text-white disabled:opacity-40 hover:bg-blue-700"
            >
              <Plus className="w-3 h-3 inline mr-0.5" />
              Add example
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const FineTuning = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  // Per-project override: when scopeProjectId is empty, we're editing the
  // workspace default. When set, we GET/PUT the project row (with fallback
  // to the workspace default on first open — see _scope_source).
  const [projects, setProjects] = useState([]);
  // Preselect from ?project_id=<id> so /settings?tab=fine-tuning&project_id=…
  // (used by the ProjectDetail "Edit fine tuning" button) lands on the
  // right project's settings.
  const initialProjectId = (() => {
    if (typeof window === "undefined") return "";
    const sp = new URLSearchParams(window.location.search);
    return sp.get("project_id") || "";
  })();
  const [scopeProjectId, setScopeProjectId] = useState(initialProjectId);
  const [scopeSource, setScopeSource] = useState("workspace");

  useEffect(() => {
    v2Api.projects.list().then(setProjects).catch(() => {});
  }, []);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeProjectId]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const params = scopeProjectId ? { project_id: scopeProjectId } : {};
      const response = await axios.get(
        `${API}/api/v2/settings/fine-tuning`,
        { params }
      );
      const data = response.data || {};
      setScopeSource(data._scope_source || "workspace");
      // Drop the helper key before storing — we don't want to PUT it back.
      const { _scope_source, ...rest } = data;
      setSettings(rest);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      // Set defaults
      setSettings({
        ai_temperature: 0.7,
        ai_max_tokens: 4000,
        ai_analysis_depth: 'detailed',
        scoring_weights: {
          eligibility_match: 15,
          technical_match: 20,
          experience_match: 15,
          financial_match: 15,
          timeline_fit: 10,
          resource_availability: 10,
          compliance_score: 15
        },
        company_profile: {
          company_name: 'AXISINDIA',
          years_experience: 15,
          employee_count: 500,
          certifications: ['ISO 9001', 'ISO 14001', 'OHSAS 18001'],
          key_industries: ['Oil & Gas', 'Water Treatment', 'Power', 'Chemicals'],
          min_project_value: 100000,
          max_project_value: 50000000,
          geographic_presence: ['India', 'Middle East', 'Southeast Asia']
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      const keys = path.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setHasChanges(true);
  };

  const updateScoringWeight = (key, value) => {
    setSettings(prev => ({
      ...prev,
      scoring_weights: {
        ...prev.scoring_weights,
        [key]: value
      }
    }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const params = scopeProjectId ? { project_id: scopeProjectId } : {};
      await axios.put(
        `${API}/api/v2/settings/fine-tuning`,
        settings,
        { params }
      );
      toast.success(
        scopeProjectId
          ? "Project override saved"
          : "Workspace default saved"
      );
      setHasChanges(false);
      // Re-fetch so _scope_source flips from 'workspace' (fallback) to
      // 'project' (real override) after the first save on a project.
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const revertProjectOverride = async () => {
    if (!scopeProjectId) return;
    if (!await confirm("Drop this project's override and inherit from workspace default?", { title: "Revert to workspace default", confirmLabel: "Revert to default", destructive: false })) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/api/v2/settings/fine-tuning`, {
        params: { project_id: scopeProjectId },
      });
      toast.success("Reverted to workspace default");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to revert");
    } finally {
      setSaving(false);
    }
  };

  const getTotalWeight = () => {
    if (!settings?.scoring_weights) return 0;
    return Object.values(settings.scoring_weights).reduce((a, b) => a + b, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="h-96 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="fine-tuning">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <Sliders className="w-8 h-8 text-purple-600" />
              Fine Tuning Settings
            </h1>
            <p className="text-slate-500 mt-1">Customize AI analysis, scoring weights, and company profile</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchSettings} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Reset
            </Button>
            <Button 
              onClick={saveSettings} 
              disabled={saving || !hasChanges}
              className="bg-[#007AFF]"
            >
              <Save className={`w-4 h-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
              Save Changes
            </Button>
          </div>
        </div>

        {/* Scope selector — Workspace default vs Project override.
            Picking a project shows that project's override if one exists,
            or the workspace default with an "Inheriting" badge until you
            save a change for the project. */}
        <Card>
          <CardContent className="py-3 px-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-slate-700">Scope:</span>
            <button
              type="button"
              onClick={() => setScopeProjectId("")}
              className={`px-3 py-1.5 rounded-md text-sm transition ${
                !scopeProjectId
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Workspace default
            </button>
            <span className="text-slate-300">|</span>
            <span className="text-sm text-slate-600">Per-project:</span>
            <Select
              value={scopeProjectId || "_none"}
              onValueChange={(v) => setScopeProjectId(v === "_none" ? "" : v)}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="Pick a project to override…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— None (workspace default) —</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {scopeProjectId && scopeSource === "workspace" && (
              <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                Inheriting from workspace — Save to create override
              </Badge>
            )}
            {scopeProjectId && scopeSource === "project" && (
              <>
                <Badge className="bg-emerald-100 text-emerald-700">
                  Project override active
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={revertProjectOverride}
                  disabled={saving}
                >
                  Revert to workspace default
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {hasChanges && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-amber-700">You have unsaved changes</span>
          </div>
        )}

        <Tabs defaultValue="ai" className="space-y-4">
          <TabsList className="bg-white border w-full justify-start">
            <TabsTrigger value="ai" className="flex items-center gap-2">
              <Brain className="w-4 h-4" /> AI Parameters
            </TabsTrigger>
            <TabsTrigger value="scoring" className="flex items-center gap-2">
              <Target className="w-4 h-4" /> Scoring Weights
            </TabsTrigger>
            {/* Company Profile tab hidden — edit via Settings > Company */}
          </TabsList>

          {/* AI Parameters */}
          <TabsContent value="ai">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-600" />
                  AI Analysis Parameters
                </CardTitle>
                <CardDescription>Configure how the AI analyzes RFP documents</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Temperature */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">AI Temperature</label>
                    <Badge variant="outline">{settings?.ai_temperature || 0.7}</Badge>
                  </div>
                  <Slider
                    value={[settings?.ai_temperature * 100 || 70]}
                    onValueChange={([v]) => updateSetting('ai_temperature', v / 100)}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Lower = More focused/consistent. Higher = More creative/varied.
                  </p>
                </div>

                {/* Max Tokens */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Max Analysis Tokens</label>
                  <Select 
                    value={String(settings?.ai_max_tokens || 4000)}
                    onValueChange={(v) => updateSetting('ai_max_tokens', parseInt(v))}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2000">2000 (Faster, less detail)</SelectItem>
                      <SelectItem value="4000">4000 (Balanced)</SelectItem>
                      <SelectItem value="8000">8000 (More detail)</SelectItem>
                      <SelectItem value="16000">16000 (Maximum detail)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Analysis Depth */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Analysis Depth</label>
                  <Select 
                    value={settings?.ai_analysis_depth || 'detailed'}
                    onValueChange={(v) => updateSetting('ai_analysis_depth', v)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quick">Quick (Basic extraction)</SelectItem>
                      <SelectItem value="standard">Standard (Key points + summary)</SelectItem>
                      <SelectItem value="detailed">Detailed (Full analysis + recommendations)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scoring Weights */}
          <TabsContent value="scoring">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  Bid Matrix Scoring Weights
                </CardTitle>
                <CardDescription>
                  Adjust how much each factor contributes to the overall bid score
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Total indicator */}
                <div className={`p-4 rounded-lg ${getTotalWeight() === 100 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Total Weight:</span>
                    <Badge className={getTotalWeight() === 100 ? 'bg-emerald-600' : 'bg-red-600'}>
                      {getTotalWeight()}%
                    </Badge>
                  </div>
                  {getTotalWeight() !== 100 && (
                    <p className="text-xs text-red-600 mt-1">Weights must equal 100%</p>
                  )}
                </div>

                {/* Weight Sliders */}
                {settings?.scoring_weights && Object.entries(settings.scoring_weights).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-slate-700 capitalize">
                        {key.replace(/_/g, ' ')}
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={value}
                          onChange={(e) => updateScoringWeight(key, parseInt(e.target.value) || 0)}
                          className="w-20 text-right"
                        />
                        <Percent className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                    <Slider
                      value={[value]}
                      onValueChange={([v]) => updateScoringWeight(key, v)}
                      max={50}
                      step={5}
                      className="w-full"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Company Profile */}
          <TabsContent value="profile">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-teal-600" />
                  Company Profile for Bid Matching
                </CardTitle>
                <CardDescription>
                  This information is used to match against RFP requirements
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Company Name */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Company Name</label>
                  <Input
                    value={settings?.company_profile?.company_name || ''}
                    onChange={(e) => updateSetting('company_profile.company_name', e.target.value)}
                  />
                </div>

                {/* Experience & Employees */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      <Clock className="w-4 h-4" /> Years of Experience
                    </label>
                    <Input
                      type="number"
                      value={settings?.company_profile?.years_experience || 0}
                      onChange={(e) => updateSetting('company_profile.years_experience', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      <Users className="w-4 h-4" /> Employee Count
                    </label>
                    <Input
                      type="number"
                      value={settings?.company_profile?.employee_count || 0}
                      onChange={(e) => updateSetting('company_profile.employee_count', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {/* Project Value Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> Min Project Value (₹)
                    </label>
                    <Input
                      type="number"
                      value={settings?.company_profile?.min_project_value || 0}
                      onChange={(e) => updateSetting('company_profile.min_project_value', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                      <DollarSign className="w-4 h-4" /> Max Project Value (₹)
                    </label>
                    <Input
                      type="number"
                      value={settings?.company_profile?.max_project_value || 0}
                      onChange={(e) => updateSetting('company_profile.max_project_value', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>

                {/* Certifications */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <Award className="w-4 h-4" /> Certifications (comma-separated)
                  </label>
                  <Input
                    value={settings?.company_profile?.certifications?.join(', ') || ''}
                    onChange={(e) => updateSetting('company_profile.certifications', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="ISO 9001, ISO 14001, OHSAS 18001"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {settings?.company_profile?.certifications?.map((cert, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                    ))}
                  </div>
                </div>

                {/* Key Industries */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Key Industries (comma-separated)</label>
                  <Input
                    value={settings?.company_profile?.key_industries?.join(', ') || ''}
                    onChange={(e) => updateSetting('company_profile.key_industries', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="Oil & Gas, Water Treatment, Power"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {settings?.company_profile?.key_industries?.map((ind, i) => (
                      <Badge key={i} className="bg-blue-100 text-blue-700 text-xs">{ind}</Badge>
                    ))}
                  </div>
                </div>

                {/* Geographic Presence */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> Geographic Presence (comma-separated)
                  </label>
                  <Input
                    value={settings?.company_profile?.geographic_presence?.join(', ') || ''}
                    onChange={(e) => updateSetting('company_profile.geographic_presence', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    placeholder="India, Middle East, Southeast Asia"
                  />
                  <div className="flex flex-wrap gap-1 mt-2">
                    {settings?.company_profile?.geographic_presence?.map((loc, i) => (
                      <Badge key={i} className="bg-teal-100 text-teal-700 text-xs">{loc}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default FineTuning;
